/**
 * Backup and recovery, in Settings.
 *
 * The warning comes first and is plain, because it is the one fact about this
 * app a parent most needs and least expects: the data is encrypted with a key
 * Valley never has, so losing the family code and every device loses the
 * family. Then the two things that prevent it -- the printed sheet and the
 * backup file -- and the one that repairs it: restoring a backup into a new
 * family. See family/backup.ts.
 */

import { useState } from 'react';
import {
  downloadBackup,
  makeBackup,
  parseBackup,
  restoreBackup,
  summarise,
  type Backup,
} from '../family/backup';
import { loadState, type Person } from '../family/state';
import { dollars } from '../family/money';
import { RecoverySheet, noteRecoverySaved, recoverySaved } from './RecoverySheet';

export function BackupSection({
  familyId,
  familyKey,
  familyName,
  people,
  onRestored,
}: {
  familyId: string;
  familyKey: CryptoKey;
  familyName: string;
  people: Person[];
  /** After a restore, so the rest of the app reads the family again. */
  onRestored: () => void;
}) {
  const [sheet, setSheet] = useState(false);
  const [working, setWorking] = useState('');
  const [error, setError] = useState('');
  const [chosen, setChosen] = useState<Backup | null>(null);
  const [restored, setRestored] = useState(false);
  const last = recoverySaved(familyId);
  const empty = people.length === 0;

  async function download(): Promise<void> {
    setWorking('Making the backup…');
    setError('');
    try {
      const state = await loadState(familyId, familyKey);
      downloadBackup(await makeBackup(familyId, familyKey, state));
      noteRecoverySaved(familyId);
    } catch (problem) {
      setError(problem instanceof Error ? problem.message : 'The backup could not be made.');
    } finally {
      setWorking('');
    }
  }

  async function restore(backup: Backup): Promise<void> {
    setError('');
    try {
      await restoreBackup(familyId, familyKey, backup, (words) => setWorking(`Restoring: ${words}…`));
      setChosen(null);
      setRestored(true);
      onRestored();
    } catch (problem) {
      setError(
        `${problem instanceof Error ? problem.message : 'The restore stopped.'} ` +
          'Nothing is lost: run the restore again with the same file and it carries on.',
      );
    } finally {
      setWorking('');
    }
  }

  const summary = chosen ? summarise(chosen) : null;

  return (
    <section id="backup">
      <p className="field-label">Backup and recovery</p>
      <div className="danger-box">
        <p>
          <strong>Keep your family code safe.</strong> Everything in Birbly is encrypted with your
          family&rsquo;s key, which Birbly never has. If you lose your family code <em>and</em> every
          device that is signed in, nobody &mdash; not even Birbly &mdash; can open your family&rsquo;s
          money, goals or names again.
        </p>
      </div>
      <p className="footnote">
        {last ? `Last printed or saved on this device ${last}.` : 'Not printed or saved on this device yet.'}
      </p>
      <div className="actions">
        <button type="button" className="primary" onClick={() => setSheet(true)}>
          Print the recovery sheet
        </button>
        <button type="button" disabled={Boolean(working)} onClick={() => void download()}>
          Download a backup file
        </button>
      </div>
      <p className="footnote">
        The sheet has your family code and each child&rsquo;s code. The backup file has everything
        &mdash; names, money, goals &mdash; <strong>readable</strong>, so it can be restored even
        without the code. Keep it where you would keep a bank statement.
      </p>

      <p className="field-label">Restore from a backup</p>
      {empty ? (
        chosen && summary ? (
          <div className="record">
            <p>
              This backup is of <strong>{chosen.familyName || 'a family'}</strong>, made{' '}
              {chosen.exportedAt.slice(0, 10)}: {summary.people} people, {summary.entries} entries,{' '}
              {summary.goals} goals, and {dollars(summary.balance)} in all.
            </p>
            <p className="footnote">
              Everything goes into this family. Each child gets a new code &mdash; print the new
              recovery sheet afterwards, and approve each child&rsquo;s device when they type it.
            </p>
            <div className="actions">
              <button type="button" className="primary" disabled={Boolean(working)} onClick={() => void restore(chosen)}>
                Restore it
              </button>
              <button type="button" disabled={Boolean(working)} onClick={() => setChosen(null)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <label className="field">
            <span>Choose the backup file</span>
            <input
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                setError('');
                void file
                  .text()
                  .then((text) => setChosen(parseBackup(text)))
                  .catch((problem: unknown) =>
                    setError(problem instanceof Error ? problem.message : 'That file could not be read.'),
                  );
              }}
            />
            <small>For a new family made after losing everything. Nothing is sent anywhere until you say so.</small>
          </label>
        )
      ) : (
        <p className="footnote">
          Restoring is for a new, empty family &mdash; the one you make after losing every device and
          the code. This family already has people in it.
        </p>
      )}

      {working ? <p className="footnote" role="status">{working}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {restored ? (
        <p className="footnote" role="status">
          Restored. <strong>Print the new recovery sheet now</strong> &mdash; the children&rsquo;s codes
          are new.
        </p>
      ) : null}

      {sheet ? (
        <RecoverySheet
          familyId={familyId}
          familyKey={familyKey}
          familyName={familyName}
          people={people.filter((p) => p.status === 'approved')}
          onClose={() => setSheet(false)}
        />
      ) : null}
    </section>
  );
}
